import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  BUNDLE_PURGE_FAILED_EVENT,
  BUNDLE_SAFE_MODE_EVENT,
  exitSafeMode,
  forceBundleReload,
  getSafeModeState,
  MAX_PURGE_ATTEMPTS,
  type BundlePurgeFailedDetail,
  type SafeModeState,
} from "@/lib/bundle-version";

/**
 * Last-resort recovery UI for the embedded-bundle updater.
 *
 * Two levels:
 *  1. A partial cleanup failure fires `bc:bundle-purge-failed` — we show a
 *     dismissible toast with a manual "Restart now" action.
 *  2. After MAX_PURGE_ATTEMPTS failed cycles the library stops reloading and
 *     fires `bc:bundle-safe-mode` — we take over the screen with a plain,
 *     non-technical choice: continue in safe mode, or try cleanup once more.
 */
export function BundleUpdateFallback() {
  const [detail, setDetail] = useState<BundlePurgeFailedDetail | null>(null);
  const [safeMode, setSafeMode] = useState<SafeModeState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSafeMode(getSafeModeState());
    const onFail = (event: Event) => {
      setDetail((event as CustomEvent<BundlePurgeFailedDetail>).detail ?? null);
    };
    const onSafe = (event: Event) => {
      setSafeMode((event as CustomEvent<SafeModeState>).detail ?? null);
    };
    window.addEventListener(BUNDLE_PURGE_FAILED_EVENT, onFail);
    window.addEventListener(BUNDLE_SAFE_MODE_EVENT, onSafe);
    return () => {
      window.removeEventListener(BUNDLE_PURGE_FAILED_EVENT, onFail);
      window.removeEventListener(BUNDLE_SAFE_MODE_EVENT, onSafe);
    };
  }, []);

  useEffect(() => {
    if (!detail || safeMode) return;
    const id = toast.warning("App update needs a restart", {
      description: "We couldn't finish clearing the old app data automatically.",
      duration: Infinity,
      action: {
        label: busy ? "Restarting…" : "Restart now",
        onClick: () => {
          if (busy) return;
          setBusy(true);
          void forceBundleReload();
        },
      },
      onDismiss: () => setDetail(null),
    });
    return () => {
      toast.dismiss(id);
    };
  }, [detail, safeMode, busy]);

  if (!safeMode) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm space-y-5 rounded-xl border border-border bg-card p-6 text-center shadow-lg">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold text-card-foreground">Safe mode</h1>
          <p className="text-sm text-muted-foreground">
            We tried {safeMode.attempts} of {MAX_PURGE_ATTEMPTS} times to finish updating the app and
            couldn't complete it. You can keep using the app in safe mode, or try the cleanup once more.
          </p>
        </div>

        <div className="space-y-2">
          <Button
            className="w-full"
            onClick={() => {
              exitSafeMode();
              setSafeMode(null);
              setDetail(null);
            }}
          >
            Start in safe mode
          </Button>
          <Button
            variant="outline"
            className="w-full"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              exitSafeMode({ retry: true });
              void forceBundleReload();
            }}
          >
            {busy ? "Cleaning up…" : "Try cleanup again"}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          If this keeps happening, reinstalling the app from the latest download will fix it.
        </p>
      </div>
    </div>
  );
}

export default BundleUpdateFallback;
